import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Wordmark from "@/components/Wordmark";
import { TVPlayer } from "@/components/TVPlayer";
import { Play, LogOut, Gamepad2 } from "lucide-react";

const fmtViews = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}K` : n) + " views";

export default function TV() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [pos, setPos] = useState({ r: 0, c: 0 });
  const [playing, setPlaying] = useState(null);
  const tiles = useRef({});

  useEffect(() => { api.get("/tv/feed").then(r => setRows(r.data.rows)).catch(() => setRows([])); }, []);

  const move = useCallback((dr, dc) => setPos(p => {
    const r = Math.max(0, Math.min(rows.length - 1, p.r + dr));
    const len = rows[r]?.items.length || 1;
    return { r, c: Math.max(0, Math.min(len - 1, dr ? Math.min(p.c, len - 1) : p.c + dc)) };
  }), [rows]);

  useEffect(() => {
    if (playing) return;
    const onKey = (e) => {
      const k = e.key;
      if (k === "ArrowRight") move(0, 1); else if (k === "ArrowLeft") move(0, -1);
      else if (k === "ArrowDown") move(1, 0); else if (k === "ArrowUp") move(-1, 0);
      else if (k === "Enter" || k === " ") { const it = rows[pos.r]?.items[pos.c]; if (it) setPlaying(it); }
      else if (k === "Escape" || k === "Backspace" || k === "GoBack") nav("/");
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [move, rows, pos, playing, nav]);

  const rowsBox = useRef(null);
  const rowEls = useRef({});
  useEffect(() => {
    const tile = tiles.current[`${pos.r}-${pos.c}`], strip = tile?.parentElement;
    if (strip) strip.scrollTo({ left: tile.offsetLeft - strip.clientWidth / 2 + tile.clientWidth / 2, behavior: "smooth" });
    const row = rowEls.current[pos.r];
    if (row && rowsBox.current) rowsBox.current.scrollTo({ top: row.offsetTop, behavior: "smooth" });
  }, [pos]);

  const focused = rows[pos.r]?.items[pos.c];

  return (
    <div className="fixed inset-0 bg-[#050510] text-white overflow-hidden select-none" data-testid="tv-page">
      {focused && <img key={focused.id} src={focused.thumbnail || undefined} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 blur-sm scale-105 transition-opacity duration-500"/>}
      <div className="absolute inset-0 bg-gradient-to-t from-[#050510] via-[#050510]/85 to-[#050510]/40"/>

      <div className="relative h-full flex flex-col">
        <header className="flex items-center justify-between px-[4vw] pt-[3vh]">
          <Wordmark size="md"/>
          <div className="flex items-center gap-4 text-white/50 text-sm">
            <span className="hidden md:inline-flex items-center gap-2"><Gamepad2 size={16}/> Use your remote: arrows to browse · OK to play · Back to exit</span>
            <button onClick={() => nav("/")} data-testid="tv-exit" className="jx-btn h-10 px-4 rounded-full border border-white/15 inline-flex items-center gap-2 text-white/80"><LogOut size={14}/> Exit TV</button>
          </div>
        </header>

        <section className="px-[4vw] pt-[4vh] h-[34vh] max-w-[60vw]" data-testid="tv-hero">
          {focused ? (<>
            <div className="text-[#00E7FF] text-sm font-bold tracking-widest uppercase">{rows[pos.r].title}</div>
            <h1 className="h-font font-extrabold text-4xl lg:text-6xl mt-2 line-clamp-2" data-testid="tv-focused-title">{focused.title}</h1>
            <div className="text-white/60 text-lg mt-3">@{focused.creator} · {fmtViews(focused.views)} · {focused.category}</div>
            {focused.description && <p className="text-white/50 text-base mt-3 line-clamp-2">{focused.description}</p>}
          </>) : <div className="text-white/50 text-xl">{rows.length ? "" : "Loading Jixxeo…"}</div>}
        </section>

        <div ref={rowsBox} className="relative flex-1 overflow-y-hidden overflow-x-hidden pb-[60vh]" data-testid="tv-rows">
          {rows.map((row, r) => (
            <div key={row.title} ref={el => (rowEls.current[r] = el)} className="pt-[2vh]" data-testid={`tv-row-${r}`}>
              <h2 className={`px-[4vw] text-lg font-bold transition-colors ${r === pos.r ? "text-white" : "text-white/40"}`}>{row.title}</h2>
              <div className="relative flex gap-5 overflow-x-hidden px-[4vw] py-5">
                {row.items.map((it, c) => {
                  const on = r === pos.r && c === pos.c;
                  const loop = it.format === "short";
                  return (
                    <button key={it.id} ref={el => (tiles.current[`${r}-${c}`] = el)} data-testid={`tv-tile-${r}-${c}`}
                      onClick={() => { setPos({ r, c }); setPlaying(it); }} onMouseEnter={() => setPos({ r, c })}
                      className={`relative shrink-0 rounded-2xl overflow-hidden bg-black transition-[transform,box-shadow] duration-200 ${loop ? "w-[11vw] aspect-[9/16]" : "w-[22vw] aspect-video"} ${on ? "scale-110 z-10 ring-4 ring-[#00E7FF] shadow-[0_0_40px_rgba(0,231,255,0.55)]" : "opacity-70"}`}>
                      {it.thumbnail && <img src={it.thumbnail} alt={it.title} className="w-full h-full object-cover"/>}
                      {on && <span className="absolute inset-0 flex items-center justify-center bg-black/20"><span className="w-14 h-14 rounded-full bg-[#00E7FF] flex items-center justify-center"><Play size={24} className="text-black ml-1"/></span></span>}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {playing && <TVPlayer item={playing} onClose={() => setPlaying(null)}/>}
    </div>
  );
}
